import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Header from './Header';
import Footer from './Footer';
import HotelStripePaymentForm from './HotelStripePaymentForm';
import { formatCurrency, getCurrency, convertCurrency } from './utils/currency';
import './HotelBookingPage.css';

// Must match Supabase secret STRIPE_SECRET_KEY: pk_test_… + sk_test_… (test) or pk_live_… + sk_live_… (live)
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const HotelBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hotel, offer, searchParams } = location.state || {};
  
  // Preserve hotel and offer data in state to prevent loss on re-render
  const [preservedHotel, setPreservedHotel] = useState(hotel);
  const [preservedOffer, setPreservedOffer] = useState(offer);
  const [preservedSearchParams, setPreservedSearchParams] = useState(searchParams);
  
  // Update preserved state when location.state changes
  useEffect(() => {
    if (hotel) setPreservedHotel(hotel);
    if (offer) setPreservedOffer(offer);
    if (searchParams) setPreservedSearchParams(searchParams);
  }, [hotel, offer, searchParams]);
  
  // Use preserved data if location.state is lost
  const activeHotel = hotel || preservedHotel;
  const activeOffer = offer || preservedOffer;
  const activeSearchParams = searchParams || preservedSearchParams;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    specialRequests: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [isProcessingBooking, setIsProcessingBooking] = useState(false);
  const [isInitializingPayment, setIsInitializingPayment] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    // Clear any errors on initial load
    setError(null);
    
    // Don't redirect if we're processing a booking, initializing payment, or user has interacted
    if (isProcessingBooking || isInitializingPayment || loading || hasUserInteracted) {
      console.log('[Hotel Booking] Operation in progress or user interaction, skipping redirect check');
      return;
    }

    // Only redirect if hotel/offer is missing on initial load (not during user interaction)
    // Use activeHotel and activeOffer to check preserved state
    if (!activeHotel || !activeOffer) {
      console.warn('[Hotel Booking] Missing hotel or offer data on initial load, redirecting to hotels page');
      navigate('/hotels');
      return;
    }

    console.log('[Hotel Booking] Page loaded with hotel and offer data:', {
      hotelId: activeHotel?.hotelId,
      hotelName: activeHotel?.name,
      offerId: activeOffer?.id,
      hasSearchParams: !!activeSearchParams
    });

    const initCurrency = async () => {
      try {
        const userCurrency = getCurrency() || { code: 'NGN', symbol: '₦' };
        setCurrency(userCurrency);
      } catch (error) {
        console.error('Currency initialization error:', error);
        setCurrency({ code: 'NGN', symbol: '₦' });
      }
    };
    
    initCurrency();
  }, [hotel, offer, navigate, searchParams, isProcessingBooking, isInitializingPayment, loading, hasUserInteracted]);

  // Auto-initialize Stripe payment intent when Stripe is selected and form is valid
  // Only initialize if user has interacted with the form (to avoid showing errors on page load)
  useEffect(() => {
    // Only auto-initialize if:
    // 1. Stripe is selected
    // 2. User has interacted with the form (hasUserInteracted is true)
    // 3. Form is valid (has required fields)
    // 4. Payment intent hasn't been initialized yet
    // 5. Not currently initializing or processing
    if (
      paymentMethod === 'stripe' &&
      hasUserInteracted &&
      !stripeClientSecret &&
      !isInitializingPayment &&
      !loading &&
      !isProcessingBooking &&
      formData.firstName &&
      formData.lastName &&
      formData.email &&
      formData.phone
    ) {
      console.log('[Hotel Booking] Auto-initializing Stripe payment intent (form is valid and user has interacted)');
      // Small delay to avoid race conditions
      const timer = setTimeout(() => {
        // Use silent validation for auto-initialization (don't show errors)
        initializeStripePayment(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [paymentMethod, hasUserInteracted, stripeClientSecret, isInitializingPayment, loading, isProcessingBooking, formData.firstName, formData.lastName, formData.email, formData.phone, offer, hotel]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Mark that user has interacted
    setHasUserInteracted(true);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const formatPriceDisplay = (price) => {
    if (!price || !price.total) return 'Price not available';
    
    const userCurrency = currency || getCurrency() || { code: 'NGN', symbol: '₦' };
    const apiCurrency = (price.currency || searchParams?.currency || 'NGN').toUpperCase();
    const apiAmount = parseFloat(price.total);
    
    if (isNaN(apiAmount) || apiAmount <= 0) {
      return 'Price not available';
    }
    
    if (apiCurrency === userCurrency.code) {
      return formatCurrency(apiAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    }
    
    try {
      const convertedAmount = convertCurrency(apiAmount, apiCurrency, userCurrency.code);
      
      if (!convertedAmount || convertedAmount <= 0) {
        return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      
      return formatCurrency(convertedAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    } catch (error) {
      return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateNights = () => {
    if (!searchParams?.checkInDate || !searchParams?.checkOutDate) return 0;
    const checkIn = new Date(searchParams.checkInDate);
    const checkOut = new Date(searchParams.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const validateForm = (showErrors = true) => {
    // Only show errors if explicitly requested (when user tries to submit)
    // Don't show errors on auto-validation or page load
    
    if (!formData.firstName.trim()) {
      if (showErrors) setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      if (showErrors) setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      if (showErrors) setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      if (showErrors) setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      if (showErrors) setError('Phone number is required');
      return false;
    }
    if (!formData.address.trim()) {
      if (showErrors) setError('Address is required');
      return false;
    }
    if (!formData.city.trim()) {
      if (showErrors) setError('City is required');
      return false;
    }
    if (!formData.country.trim()) {
      if (showErrors) setError('Country is required');
      return false;
    }
    // Clear errors if validation passes
    if (showErrors) setError(null);
    return true;
  };

  const initializeStripePayment = async (silentValidation = false) => {
    console.log('[Hotel Booking] initializeStripePayment called');
    
    // Mark that user has interacted
    setHasUserInteracted(true);
    
    // Validate form before initializing payment
    // Use silent validation for auto-initialization (don't show errors)
    if (!validateForm(!silentValidation)) {
      console.log('[Hotel Booking] Form validation failed, not initializing payment');
      return;
    }

    try {
      console.log('[Hotel Booking] Starting payment initialization...');
      setIsInitializingPayment(true);
      setLoading(true);
      setError(null);
      setShowStripeForm(false);
      setStripeClientSecret(null);

      console.log('[Hotel Booking] Initializing Stripe payment...');
      
      const amount = offer?.price?.total || hotel?.price?.total || 0;
      const currencyCode = offer?.price?.currency || hotel?.price?.currency || 'NGN';

      console.log('[Hotel Booking] Payment details:', { amount, currency: currencyCode });

      // Supabase Edge Function URL (same as flight booking)
      const SUPABASE_FUNCTION_URL = 'https://wnsyglbyunngvlcpdhju.supabase.co/functions/v1';
      const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Induc3lnbGJ5dW5uZ3ZsY3BkaGp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU4OTUyMDEsImV4cCI6MjA4MTQ3MTIwMX0.qgSiHbYCWqo6qS9ZAsvwxk6aRr0Yq3LQnEp7XnRMHAc';

      // Create Stripe payment intent using Supabase Edge Function
      console.log('[Hotel Booking] Calling Stripe payment intent API...');
      const paymentIntentResponse = await fetch(`${SUPABASE_FUNCTION_URL}/stripe-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          amount: amount,
          currency: currencyCode.toLowerCase()
        }),
      });

      console.log('[Hotel Booking] Payment intent response status:', paymentIntentResponse.status);

      if (!paymentIntentResponse.ok) {
        const errorText = await paymentIntentResponse.text();
        console.error('[Hotel Booking] Payment intent API error:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.error || 'Failed to initialize payment. Please try again.');
        } catch {
          setError('Failed to initialize payment. Please try again.');
        }
        setLoading(false);
        return;
      }

      const paymentIntentData = await paymentIntentResponse.json();
      console.log('[Hotel Booking] Payment intent data:', paymentIntentData);

      if (paymentIntentData.success && paymentIntentData.clientSecret) {
        console.log('[Hotel Booking] ✅ Payment intent created successfully');
        setStripeClientSecret(paymentIntentData.clientSecret);
        setShowStripeForm(true);
        setLoading(false);
        setIsInitializingPayment(false);
        // Force a small delay to ensure state updates
        setTimeout(() => {
          setLoading(false);
        }, 100);
      } else {
        console.error('[Hotel Booking] ❌ Payment intent creation failed:', paymentIntentData);
        setError(paymentIntentData.error || 'Failed to initialize payment. Please try again.');
        setLoading(false);
        setIsInitializingPayment(false);
      }
    } catch (err) {
      console.error('[Hotel Booking] ❌ Stripe initialization error:', err);
      setError('Failed to initialize payment. Please try again.');
      setLoading(false);
      setIsInitializingPayment(false);
    }
  };

  const handleStripeSuccess = async (result) => {
    try {
      console.log('[Hotel Booking] ========== STRIPE PAYMENT SUCCESS ==========');
      console.log('[Hotel Booking] Payment result:', result);
      
      setIsProcessingBooking(true);
      setLoading(true);
      setError(null);

      // Get current hotel/offer data (use preserved state if location.state is lost)
      const currentHotel = activeHotel || hotel;
      const currentOffer = activeOffer || offer;
      const currentSearchParams = activeSearchParams || searchParams;

      if (!currentHotel || !currentOffer) {
        console.error('[Hotel Booking] ❌ Hotel or offer data missing!');
        setError('Hotel or offer data is missing. Please try again.');
        setIsProcessingBooking(false);
        return;
      }

      const amount = currentOffer?.price?.total || currentHotel?.price?.total || 0;
      const currencyCode = currentOffer?.price?.currency || currentHotel?.price?.currency || 'NGN';

      console.log('[Hotel Booking] Creating hotel booking with:', {
        amount,
        currency: currencyCode,
        hotelId: currentHotel?.hotelId,
        offerId: currentOffer?.id,
        paymentIntentId: result.payment?.paymentIntentId
      });

      // Supabase Edge Function URL
      const SUPABASE_FUNCTION_URL = 'https://wnsyglbyunngvlcpdhju.supabase.co/functions/v1';
      const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Induc3lnbGJ5dW5uZ3ZsY3BkaGp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU4OTUyMDEsImV4cCI6MjA4MTQ3MTIwMX0.qgSiHbYCWqo6qS9ZAsvwxk6aRr0Yq3LQnEp7XnRMHAc';

      // Process hotel booking with payment using Supabase Edge Function
      const bookingData = {
        amount: amount,
        currency: currencyCode,
        hotelId: hotel?.hotelId,
        offerId: offer?.id,
        stripePaymentIntentId: result.payment?.paymentIntentId,
        bookingDetails: {
          hotel: currentHotel,
          offer: currentOffer,
          searchParams: currentSearchParams,
          guestInfo: formData
        }
      };

      console.log('[Hotel Booking] Calling Supabase Edge Function: hotel-booking');
      console.log('[Hotel Booking] Booking data:', JSON.stringify(bookingData, null, 2));

      let bookingResult;

      try {
        // Call Supabase Edge Function for hotel booking
        const response = await fetch(`${SUPABASE_FUNCTION_URL}/hotel-booking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(bookingData),
        });

        console.log('[Hotel Booking] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[Hotel Booking] ❌ API Error Response (hotel-booking):', errorText);
          try {
            const errorData = JSON.parse(errorText);
            console.error('[Hotel Booking] Parsed error data:', errorData);
          } catch {
            console.error('[Hotel Booking] Could not parse error JSON from hotel-booking function.');
          }

          // Fallback: treat payment as successful and save booking locally
          console.warn('[Hotel Booking] ⚠️ hotel-booking function not reachable or returned error. Falling back to local booking.');
          const fallbackBookingId = `HTL-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
          bookingResult = {
            success: true,
            bookingId: fallbackBookingId,
            paymentDetails: {
              ...result.payment,
              provider: 'stripe',
            },
            amadeusBooking: {
              success: false,
              error: 'hotel-booking function not reachable (see console for details)',
              note: 'Stripe payment succeeded. Booking saved locally only; not confirmed with Amadeus.',
            },
            message: 'Hotel booking confirmed locally (backend booking service unavailable).',
          };
        } else {
          bookingResult = await response.json();
          console.log('[Hotel Booking] Booking result:', bookingResult);
        }
      } catch (apiErr) {
        // Network/CORS/other fetch error
        console.error('[Hotel Booking] ❌ Exception while calling hotel-booking function:', apiErr);
        console.warn('[Hotel Booking] ⚠️ hotel-booking function unreachable (network/CORS). Falling back to local booking.');
        const fallbackBookingId = `HTL-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
        bookingResult = {
          success: true,
          bookingId: fallbackBookingId,
          paymentDetails: {
            ...result.payment,
            provider: 'stripe',
          },
          amadeusBooking: {
            success: false,
            error: apiErr.message || 'hotel-booking function unreachable',
            note: 'Stripe payment succeeded. Booking saved locally only; not confirmed with Amadeus.',
          },
          message: 'Hotel booking confirmed locally (backend booking service unreachable).',
        };
      }

      if (bookingResult.success) {
        // Check if booking was created with Amadeus
        const amadeusBooking = bookingResult.amadeusBooking;
        if (amadeusBooking?.success) {
          console.log('[Hotel Booking] ✅ BOOKING CREATED WITH AMADEUS');
          console.log('[Hotel Booking] Amadeus Booking Reference:', amadeusBooking.bookingReference);
          console.log('[Hotel Booking] Amadeus Status:', amadeusBooking.status);
        } else {
          console.log('[Hotel Booking] ⚠️ BOOKING SAVED LOCALLY (Amadeus booking not completed)');
          console.log('[Hotel Booking] Amadeus Error:', amadeusBooking?.error || 'Not attempted');
          console.log('[Hotel Booking] Note:', amadeusBooking?.note || 'Payment processed, booking saved locally');
        }

        console.log('[Hotel Booking] ✅ Booking ID:', bookingResult.bookingId);
        console.log('[Hotel Booking] ✅ Payment Details:', bookingResult.paymentDetails);
        console.log('[Hotel Booking] Navigating to thank you page...');
        console.log('[Hotel Booking] ============================================');

        // Small delay to ensure state is set before navigation
        await new Promise(resolve => setTimeout(resolve, 100));

        // Redirect to thank you page with booking details
        navigate('/hotel-booking-thank-you', {
          state: {
            bookingId: bookingResult.bookingId,
            hotel: currentHotel,
            offer: currentOffer,
            guestInfo: formData,
            searchParams: currentSearchParams,
            paymentDetails: bookingResult.paymentDetails,
            amadeusBooking: amadeusBooking
          },
          replace: true // Use replace to prevent back button issues
        });
      } else {
        console.error('[Hotel Booking] ❌ Booking failed:', bookingResult.error);
        setError(bookingResult.error || 'Booking creation failed. Please try again.');
        setIsProcessingBooking(false);
      }
    } catch (err) {
      console.error('[Hotel Booking] ❌ Exception during booking:', err);
      console.error('[Hotel Booking] Error details:', {
        message: err.message,
        stack: err.stack
      });
      setError('An error occurred during booking. Please try again.');
      setIsProcessingBooking(false);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeError = (error) => {
    console.error('[Hotel Booking] ❌ Stripe payment error:', error);
    setError(error.message || 'Payment failed. Please try again.');
    setLoading(false);
  };

  const handleFlutterwavePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const amount = offer?.price?.total || hotel?.price?.total || 0;
      const currency = offer?.price?.currency || hotel?.price?.currency || 'NGN';

      // Initialize Flutterwave payment
      const paymentData = {
        amount: amount,
        currency: currency,
        hotelId: hotel?.hotelId,
        offerId: offer?.id,
        bookingDetails: {
          hotel: hotel,
          offer: offer,
          searchParams: searchParams,
          guestInfo: formData
        }
      };

      // Call backend to process Flutterwave payment
      const response = await fetch('/api/hotel/flutterwave-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const result = await response.json();

      if (result.success) {
        // Redirect to thank you page with booking details
        navigate('/hotel-booking-thank-you', {
          state: {
            bookingId: result.bookingId,
            hotel: hotel,
            offer: offer,
            guestInfo: formData,
            searchParams: searchParams,
            paymentDetails: result.paymentDetails
          }
        });
      } else {
        setError(result.error || 'Payment processing failed. Please try again.');
      }
    } catch (err) {
      console.error('Flutterwave payment error:', err);
      setError('An error occurred during payment processing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For Stripe, don't submit the parent form - let the Stripe form handle it
    if (paymentMethod === 'stripe') {
      console.log('[Hotel Booking] Stripe selected, preventing form submission');
      return false;
    }
    
    if (!validateForm()) {
      return false;
    }

    // For Flutterwave, process payment
    if (paymentMethod === 'flutterwave') {
      await handleFlutterwavePayment();
    }
    
    return false;
  };

  // Use preserved data
  const displayHotel = activeHotel || hotel;
  const displayOffer = activeOffer || offer;
  const displaySearchParams = activeSearchParams || searchParams;

  if (!displayHotel || !displayOffer) {
    return null;
  }

  const totalPrice = displayOffer?.price?.total || displayHotel?.price?.total || 0;
  const nights = calculateNights();

  return (
    <div className="hotel-booking-page">
      <Header />
      
      <div className="hotel-booking-container">
        <div className="booking-header">
          <h1>Complete Your Hotel Booking</h1>
          <p>Please fill in your details to complete the reservation</p>
        </div>

        <div className="booking-content-wrapper">
          {/* Booking Summary Card */}
          <div className="booking-summary-card">
            <h2 className="summary-title">Booking Summary</h2>
            
            <div className="summary-hotel-info">
              <h3 className="summary-hotel-name">{displayHotel.name}</h3>
              {displayHotel.rating && (
                <div className="summary-rating">
                  {'⭐'.repeat(Math.floor(displayHotel.rating))}
                  <span>{displayHotel.rating.toFixed(1)}</span>
                </div>
              )}
            </div>

            <div className="summary-room-details">
              <h4>Room Details</h4>
              <p className="room-type">
                {displayOffer.room?.type || displayOffer.room?.typeEstimated?.category || 'Standard Room'}
              </p>
              {displayOffer.room?.typeEstimated && (
                <div className="room-specs">
                  {displayOffer.room.typeEstimated.beds && (
                    <span>{displayOffer.room.typeEstimated.beds} {displayOffer.room.typeEstimated.beds === 1 ? 'Bed' : 'Beds'}</span>
                  )}
                  {displayOffer.room.typeEstimated.bedType && (
                    <span> • {displayOffer.room.typeEstimated.bedType}</span>
                  )}
                </div>
              )}
            </div>

            <div className="summary-dates">
              <div className="date-item">
                <span className="date-label">Check-in</span>
                <span className="date-value">
                  {displaySearchParams?.checkInDate ? formatDate(displaySearchParams.checkInDate) : 'N/A'}
                </span>
              </div>
              <div className="date-item">
                <span className="date-label">Check-out</span>
                <span className="date-value">
                  {displaySearchParams?.checkOutDate ? formatDate(displaySearchParams.checkOutDate) : 'N/A'}
                </span>
              </div>
              <div className="date-item">
                <span className="date-label">Nights</span>
                <span className="date-value">{nights} {nights === 1 ? 'night' : 'nights'}</span>
              </div>
            </div>

            {offer.policies?.paymentType && (
              <div className="summary-payment-info">
                <span className="payment-label">Payment:</span>
                <span className="payment-value">{offer.policies.paymentType}</span>
              </div>
            )}

            <div className="summary-price-section">
              <div className="price-breakdown">
                {offer.price?.base && (
                  <div className="price-item">
                    <span>Base Price</span>
                    <span>{formatPriceDisplay({...offer.price, total: offer.price.base})}</span>
                  </div>
                )}
                {offer.price?.taxes && (
                  <div className="price-item">
                    <span>Taxes & Fees</span>
                    <span>{formatPriceDisplay({...offer.price, total: offer.price.taxes})}</span>
                  </div>
                )}
              </div>
              <div className="total-price">
                <span className="total-label">Total Amount</span>
                <span className="total-amount">{formatPriceDisplay(offer.price || hotel.price)}</span>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div className="booking-form-card">
            <form onSubmit={handleSubmit} className="booking-form">
              <h2 className="form-title">Guest Information</h2>

              {error && (
                <div className="error-alert">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <div className="form-section">
                <h3 className="section-title">Personal Details</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">Address Information</h3>
                
                <div className="form-group">
                  <label htmlFor="address">Street Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country *</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter country"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">Postal Code</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="12345"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">Special Requests</h3>
                <div className="form-group">
                  <label htmlFor="specialRequests">Additional Notes (Optional)</label>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Any special requests or preferences..."
                  />
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">Payment Method</h3>
                <div className="payment-method-selector">
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setShowStripeForm(false);
                        setStripeClientSecret(null);
                        setError(null);
                        // Payment intent will be initialized when user clicks "Pay Now"
                      }}
                    />
                    <span className="payment-option-label">
                      <span className="payment-icon">💳</span>
                      <span>Stripe (Credit/Debit Card)</span>
                    </span>
                  </label>
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="flutterwave"
                      checked={paymentMethod === 'flutterwave'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setShowStripeForm(false);
                      }}
                    />
                    <span className="payment-option-label">
                      <span className="payment-icon">💳</span>
                      <span>Flutterwave (Card, Bank Transfer, USSD)</span>
                    </span>
                  </label>
                </div>

              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => navigate('/hotel-details', { state: { hotel, searchParams } })}
                >
                  Cancel
                </button>
                {paymentMethod === 'flutterwave' && (
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Processing Payment...' : `Pay ${formatPriceDisplay(offer.price || hotel.price)}`}
                  </button>
                )}
              </div>
            </form>

            {/* Stripe Payment Form - Outside main form to prevent submission conflicts */}
            {paymentMethod === 'stripe' && !stripePromise && (
              <div className="stripe-payment-section payment-error" style={{ marginTop: '2rem' }}>
                <p>
                  Stripe publishable key is missing. Add{' '}
                  <code>REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_…</code> to <code>.env</code> and restart the dev
                  server. It must match your Supabase <code>STRIPE_SECRET_KEY</code> (same mode: test or live).
                </p>
              </div>
            )}
            {paymentMethod === 'stripe' && stripePromise && (
              <div className="stripe-payment-section" style={{ marginTop: '2rem' }}>
                <Elements 
                  stripe={stripePromise}
                  options={stripeClientSecret ? { clientSecret: stripeClientSecret } : {}}
                >
                  <HotelStripePaymentForm
                    amount={formatPriceDisplay(offer?.price || hotel?.price)}
                    onSuccess={handleStripeSuccess}
                    onError={handleStripeError}
                    clientSecret={stripeClientSecret}
                    onInitializePayment={initializeStripePayment}
                    isInitialized={!!stripeClientSecret}
                    loading={loading}
                    error={error}
                  />
                </Elements>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HotelBookingPage;
